import { useCallback, useEffect, useState } from 'react'
import _ from 'lodash'

import { css } from '@emotion/core'
import { colors } from '@fs/zion-ui'

export const STICKY_NAV_HEIGHT = 110

export const navStyle = css`
  align-items: center;
  border-bottom: 1px solid ${colors.gray10};
  background-color: ${colors.gray00};
  display: flex;
  height: ${STICKY_NAV_HEIGHT}px;
  position: sticky;
  top: 0;
  z-index: 10000;
`

export function scrollWithOffset(el) {
  el.scrollIntoView({ behavior: 'smooth' })
}

export default function useJumpLinks({ jumpLinksRef, sections }) {
  const [activeSectionId, setActiveSectionId] = useState('')

  // This effect is needed by react-router-hash-link to scroll to hash on a full page refresh
  useEffect(() => {
    const { hash } = window.location
    if (hash) {
      const id = hash.replace('#', '')
      const scrollIntoSection = () => {
        const element = document.getElementById(id)
        if (element) {
          scrollWithOffset(element)
        }
      }

      scrollIntoSection()

      // Timeouts with extra scrolls help slower devices and are harmless to fast ones.
      setTimeout(() => {
        scrollIntoSection()
      }, 1)
      setTimeout(() => {
        scrollIntoSection()
      }, 100)
      setTimeout(() => {
        scrollIntoSection()
      }, 1000)
    }
  }, [])

  const handleScroll = useCallback(() => {
    const headerHeight = jumpLinksRef.current?.offsetHeight || 0
    const sectionsOffsets = sections.map((section) => {
      const element = document.getElementById(section.id)
      return {
        id: section.id,
        offsetTop: element.offsetTop,
        height: element.offsetHeight,
      }
    })
    const scrollPosition = window.scrollY

    // Find which section is currently at the top of the screen
    for (let i = 0; i < sectionsOffsets.length; i++) {
      const { id, offsetTop, height } = sectionsOffsets[i]
      if (scrollPosition >= offsetTop - headerHeight && scrollPosition < offsetTop + height - headerHeight) {
        setActiveSectionId(id)
        break
      } else if (scrollPosition < sectionsOffsets[0].offsetTop - headerHeight) {
        setActiveSectionId('')
      }
    }
  }, [sections, jumpLinksRef])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledOnScroll = useCallback(_.throttle(handleScroll, 100, { leading: true, trailing: true }), [
    handleScroll,
  ])

  useEffect(() => {
    window.addEventListener('scroll', throttledOnScroll)
    return () => {
      window.removeEventListener('scroll', throttledOnScroll)
    }
  }, [jumpLinksRef, sections, throttledOnScroll])

  // For accessibility, this ensures that when an element is focused, it scrolls into view if it is obscured by the sticky nav
  useEffect(() => {
    const handleFocus = (e) => {
      const headerHeight = jumpLinksRef.current?.offsetHeight || 0
      if (headerHeight > 0) {
        const el = e.target
        const rect = el?.getBoundingClientRect()
        if (!rect) return
        if (
          (rect.top < headerHeight || rect.bottom < headerHeight) &&
          jumpLinksRef.current?.getBoundingClientRect().top <= 0
        ) {
          window.scrollBy({
            top: rect.top - headerHeight,
            behavior: 'smooth',
          })
        }
      }
    }
    document.addEventListener('focusin', handleFocus)
    return () => document.removeEventListener('focusin', handleFocus)
  }, [jumpLinksRef])

  return { activeSectionId }
}
